import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "react-toastify";

import AdminLayout from "../../../layouts/AdminLayout";
import DataTableLayout from "../../../layouts/DataTableLayout";
import DataTable from "../../../components/common/DataTable";

import {
  Button,
  Modal,
  SearchInput,
  LoadingSkeleton,
  StatusBadge,
} from "../../../components/ui";
import DeleteConfirmationModal from "../../../components/common/DeleteConfirmationModal";
import SocietyForm from "../../../components/societies/SocietyForm";
import { Plus, Eye, Pencil, Trash2 } from "lucide-react";

import {
  getSocieties,
  deleteSociety,
} from "../../../services/societyService";

import { getCurrentRoleKey } from "../../../services/authService";

const ALLOWED_DELETE_ROLE_KEYS = ["super-admin", "admin"];

const SocietyManagement = () => {
  const [societies, setSocieties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [openModal, setOpenModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [viewMode, setViewMode] = useState(false);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const currentUserRoleKey = getCurrentRoleKey();

  const canDelete = useMemo(
    () => ALLOWED_DELETE_ROLE_KEYS.includes(currentUserRoleKey),
    [currentUserRoleKey]
  );

  /* ---------------- SEARCH DEBOUNCE ---------------- */
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);

    return () => clearTimeout(timer);
  }, [search]);

  /* ---------------- FETCH ---------------- */
  const fetchSocieties = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getSocieties(debouncedSearch);
      setSocieties(data || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch societies");
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    fetchSocieties();
  }, [fetchSocieties]);

  /* ---------------- HANDLERS ---------------- */
  const handleAdd = () => {
    setSelected(null);
    setViewMode(false);
    setOpenModal(true);
  };

  const handleEdit = (row) => {
    setSelected(row);
    setViewMode(false);
    setOpenModal(true);
  };

  const handleView = (row) => {
    setSelected(row);
    setViewMode(true);
    setOpenModal(true);
  };

  const handleDelete = (row) => {
    setDeleteTarget({
      row,
      type: "Society",
      id: row.society_id,
      name: row.society_name || `Society #${row.society_id}`,
      canDelete,
      actionLabel: "Deactivate",
    });
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget?.row) return;
    try {
      await deleteSociety(deleteTarget.row.society_id);
      toast.success("Society deactivated successfully.");
      fetchSocieties();
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Failed to deactivate society.");
    }
  };

  /* ---------------- COLUMNS ---------------- */
  const columns = [
    {
      header: "Sr No",
      render: (_, index) => index + 1,
    },
    {
      header: "Society Name",
      accessor: "society_name",
    },
    {
      header: "City",
      accessor: "city",
    },
    {
      header: "Location",
      render: (row) => {
        if (row.latitude && row.longitude) {
          return (
            <a
              href={`https://maps.google.com/?q=${row.latitude},${row.longitude}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline"
            >
              View Map
            </a>
          );
        }

        if (row.google_map_url) {
          return (
            <a
              href={row.google_map_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-600 underline"
            >
              Open Link
            </a>
          );
        }

        return <span className="text-gray-400">N/A</span>;
      },
    },
    {
      header: "Type & Status",
      render: (row) => (
        <div style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
          <span style={{ fontWeight: 600 }}>{row.society_type || "Residential"}</span>
          {row.property_status && row.property_status !== "operational" && (
            <StatusBadge
              variant={row.property_status === "under_construction" ? "warning" : "neutral"}
              label={row.property_status === "under_construction" ? "Under Construction" : row.property_status}
              showDot={false}
            />
          )}
        </div>
      ),
    },
    {
      header: "Hierarchy",
      render: (row) => {
        if (!row.parent_society_id) {
          return <span style={{ color: "#10b981", fontWeight: 600, fontSize: "12px" }}>Root Property</span>;
        }
        const parent = societies.find((s) => s.society_id === row.parent_society_id);
        return (
          <span style={{ color: "#6366f1", fontWeight: 500, fontSize: "12px" }}>
            ↳ Child of {parent ? parent.society_name : `#${row.parent_society_id}`}
          </span>
        );
      },
    },
    {
      header: "Address",
      accessor: "address",
    },
    {
      header: "Action",
      render: (row) => (
        <div style={{ display: "inline-flex", gap: "6px" }}>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleView(row)}
            title="View Society Details"
            icon={<Eye size={14} />}
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleEdit(row)}
            title="Edit Society"
            icon={<Pencil size={14} />}
          />
          {canDelete && (
            <Button
              variant="danger"
              size="sm"
              onClick={() => handleDelete(row)}
              title="Deactivate Society"
              icon={<Trash2 size={14} />}
            />
          )}
        </div>
      ),
    },
  ];

  return (
    <AdminLayout title="Property & Society Management">
      <DataTableLayout
        title="Society & Property Management"
        filters={
          <div style={{ minWidth: "260px" }}>
            <SearchInput
              placeholder="Search society by name / city..."
              value={search}
              onChange={(val) => setSearch(val)}
            />
          </div>
        }
        actions={
          <Button variant="primary" onClick={handleAdd} icon={<Plus size={16} />}>
            Add Society
          </Button>
        }
      >
        {loading ? (
          <div style={{ padding: "24px" }}>
            <LoadingSkeleton count={4} height="48px" style={{ marginBottom: "8px" }} />
          </div>
        ) : (
          <DataTable columns={columns} data={societies} />
        )}
      </DataTableLayout>

      {/* MODAL */}
      <Modal
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
        title={
          selected
            ? viewMode
              ? "View Society"
              : "Edit Society"
            : "Setup Society"
        }
      >
        <SocietyForm
          society={selected}
          readOnly={viewMode}
          onSuccess={() => {
            setOpenModal(false);
            fetchSocieties();
          }}
        />
      </Modal>

      <DeleteConfirmationModal
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        entityType={deleteTarget?.type || "Society"}
        entityId={deleteTarget?.id}
        entityName={deleteTarget?.name || ""}
        canDelete={deleteTarget?.canDelete !== false}
        actionLabel={deleteTarget?.actionLabel || "Deactivate"}
        onConfirm={handleConfirmDelete}
      />
    </AdminLayout>
  );
};

export default SocietyManagement;
